import "./UserManagementListComponent.scss";
import commonService from "../../../shared/services/common.service";
import React, {useCallback} from "react";
import SwitchComponent from "../../../shared/components/core/switch/switchComponent";
import {ITableColumn} from "../../../shared/models/table.model";
import {IUser} from "../../../shared/models/user.model";
import {CommonService} from "../../../shared/services";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {APIConfig, ImageConfig} from "../../../constants";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {Link} from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import ChipComponent from "../../../shared/components/chip/ChipComponent";

interface UserManagementListComponentProps {
    filterData: any;
    isColumnFilterVisible?: boolean;
}

const MODULE_NAME = "UserManagementListComponent";

const UserManagementListComponent = (props: UserManagementListComponentProps) => {
    const {filterData, isColumnFilterVisible} = props;

    const {currentUser} = useSelector((state: IRootReducerState) => state.account);


    const columns: ITableColumn[] = [
        {
            id: 'id',
            label: 'ID',
            isDefaultColumn: true,
            fixed: 'left',
            width: '150',
            content: (item: IUser) => (
                <>
                    {item?.id || '-'}
                </>
            )
        },
        {
            id: 'userName',
            label: 'User Name',
            isDefaultColumn: true,
            content: (item: IUser) => (
                <>
                    {commonService.generateNameFromNameObject(item) || '-'}
                </>
            )
        },
        {
            id: 'gender',
            label: 'Gender',
            isDefaultColumn: false,
            align: 'center',
            content: (item: IUser) => (
                <>
                    {item?.gender || '-'}
                </>
            )

        },
        {
            id: 'mobileNumber', label: 'Mobile Number', isDefaultColumn: true, align: 'center',
            content: (item: IUser) => (
                item.mobileNumber ?
                    <>
                        {CommonService.getMaskedMobileNumber(item.mobileNumber)}
                    </> : <>-</>
            )
        },
        {
            id: 'emailId',
            label: 'Email ID',
            isDefaultColumn: true,
            align: 'left',
            width: '250',
            content: (item: IUser) => (
                <>
                    {item.emailId || '-'}
                </>
            )
        },
        {
            id: 'role',
            label: 'Role',
            isDefaultColumn: true,
            width: '150',
            content: (item: IUser) => (
                <>
                    {item?.roleObj?.title || '-'}
                </>
            )
        },
        {
            id: 'facility',
            label: 'Facility',
            isDefaultColumn: true,
            width: '150',
            content: (item: IUser) => {
                const {facility} = item;

                // If facility is an array with items, display first facility and remaining count
                if (Array.isArray(facility) && facility.length > 0) {
                    const firstFacility = facility[0].name || '-';
                    const remainingCount = facility.length - 1;

                    return (
                        <div className="d-flex-wrap align-items-center column-gap-3">
                            <span>{firstFacility}</span>
                            {remainingCount > 0 && (
                                <TooltipComponent
                                    tooltip={facility.slice(1).map((fac, index) => (
                                        <ChipComponent key={index} label={fac?.name || '-'}/>
                                    ))}
                                >
                                    <div className="link-item">+({remainingCount}) more</div>
                                </TooltipComponent>
                            )}
                        </div>
                    );
                }

                // Default case: when facility is not an array or it's an object
                return <>{facility?.name || '-'}</>;
            }
        }, {
            id: 'location',
            label: 'Location',
            isDefaultColumn: true,
            width: '180',
            content: (item: IUser) => {
                const {location} = item;

                // If location is an array with items, display first location and remaining count
                if (Array.isArray(location) && location.length > 0) {
                    const firstLocation = location[0].name || '-';
                    const remainingCount = location.length - 1;

                    return (
                        <div className="d-flex-wrap align-items-center column-gap-3">
                            <span>{firstLocation}</span>
                            {remainingCount > 0 && (
                                <TooltipComponent
                                    tooltip={location.slice(1).map((loc, index) => (
                                        <ChipComponent key={index} label={loc?.name || '-'}/>
                                    ))}
                                >
                                    <div className="link-item">+({remainingCount}) more</div>
                                </TooltipComponent>
                            )}
                        </div>
                    );
                }

                // Default case: when location is not an array or it's an object
                return <>{location?.name || '-'}</>;
            }
        }, {
            id: 'lab',
            label: 'Lab',
            isDefaultColumn: true,
            width: '150',
            content: (item: IUser) => (
                <>
                    {item?.lab?.name || '-'}
                </>
            )
        },
        {
            id: 'createdAt', label: 'Added On', align: 'center',
            isDefaultColumn: false,
            content: (item: any) => (
                <>
                    {
                        item.createdAt ?
                            <>
                                {commonService.formatDate(item?.createdAt)}
                                &nbsp;|&nbsp;
                                {commonService.formatTime(item?.createdAt)} <br/>
                            </> : <> - </>
                    }
                </>

            )
        },
        {
            id: 'addedBy', label: 'Added by', isDefaultColumn: false, align: 'center',
            content: (item: IUser) => (
                <>
                    {commonService.generateNameFromNameObject(item?.createdByDetails) || '-'}
                </>
            )
        },
        {
            id: 'isActive',
            label: commonService.checkAccess('User',
                "userToggle", currentUser?.userAccessModules) ?
                'Active/Inactive' : "",
            width: commonService.checkAccess('User',
                "userToggle", currentUser?.userAccessModules) ?
                "150" : "10",
            align: "center",
            isDefaultColumn: true,
            content: (item: IUser, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('User',
                            "userToggle", currentUser?.userAccessModules) ?
                            <SwitchComponent
                                size={"small"}
                                checked={item.isActive}
                                disabled={(item?.status === 'draft')}
                                onUpdate={() => handleUserToggle(item, index)}/> :
                            <></>
                    }
                </React.Fragment>
            )
        },
        // {
        //     id: 'isActive',
        //     label: 'Active/Inactive',
        //     width: "150",
        //     align: "center",
        //     isDefaultColumn: true,
        //     content: (item: IUser, index: any) => (
        //         <React.Fragment>
        //             <SwitchComponent
        //                 size={"small"}
        //                 checked={item.isActive}
        //                 disabled={(item?.status === 'draft')}
        //                 onUpdate={() => handleUserToggle(item, index)}/>
        //         </React.Fragment>
        //     )
        // },
        {
            id: 'actions',
            label: '',
            isDefaultColumn: true,
            hideInColumnPreferences: true,
            align: 'center',
            width: "100",
            fixed: "right",
            content: (item: IUser, index: any) => {
                const hasRole = item?.roleObj;
                return <React.Fragment>
                    {CommonService.isReadOnlyFeature()?<div className="table-actions" id={`view_patient_` + index}>
                        <Link
                            to={hasRole ? CommonService._routeConfig.UserDetailsRoute(item?.id) : CommonService._routeConfig.AddUserRoute() + CommonService.setUrlSearchParams({
                                userId: item?.id,
                                step: "permissions",
                            })}>
                            <div className="table-action">
                                <ChevronRightIcon/>
                            </div>
                        </Link>
                    </div>:'-'}
                </React.Fragment>
            }
        },
    ];

    const onToggleUser = useCallback((item: any, index: any) => {
        CommonService._user.userToggleStatus(item?.id, {})
            .then((response: IAPIResponseType<any>) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                CommonService._alert.showToast(response?.message || `User has been  ${item?.isActive ? "deactivated" : "activated"}`, "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the user`, "error");
            });
    }, []);


    const handleUserToggle = useCallback((item: any, index: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this user?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: 'primary'
            },
            no: {
                text: "Cancel",
                color: 'primary'
            }
        })
            .then(() => {
                onToggleUser(item, index);
            })
            .catch(() => {
            })
    }, [onToggleUser]);

    return (
        <div className={'user-management-list-component'}>
            <TableWrapperV2Component
                isPaginated={true}
                isHoverable={true}
                isColumnFilterVisible={isColumnFilterVisible}
                columns={columns}
                url={APIConfig.USER_PAGINATED_LIST.URL}
                method={APIConfig.USER_PAGINATED_LIST.METHOD}
                extraPayload={filterData}
                moduleName={MODULE_NAME}
            />
        </div>
    );

};

export default UserManagementListComponent;
